import { useEffect, useState, type RefObject } from 'react';

export type VisibleRange = { first: number; last: number };

/**
 * The lowest and highest `data-index` among the rows currently visible inside
 * a scroll container, or null before the first observation. Rows are the
 * container's descendants carrying a numeric `data-index`; they are collected
 * again whenever `rowsKey` changes, so pass a value that changes with the
 * rendered rows.
 */
export function useVisibleRange(
  containerRef: RefObject<HTMLElement>,
  rowsKey: unknown,
): VisibleRange | null {
  const [range, setRange] = useState<VisibleRange | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const visible = new Set<number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = Number((entry.target as HTMLElement).dataset.index);
          if (entry.isIntersecting) visible.add(index);
          else visible.delete(index);
        }
        if (visible.size === 0) return;

        const first = Math.min(...visible);
        const last = Math.max(...visible);
        setRange((prev) =>
          prev?.first === first && prev?.last === last ? prev : { first, last },
        );
      },
      { root: container },
    );

    container
      .querySelectorAll<HTMLElement>('[data-index]')
      .forEach((row) => observer.observe(row));

    return () => observer.disconnect();
  }, [containerRef, rowsKey]);

  return range;
}
